import {
	CalendarOutlined,
	ContactsOutlined,
	DeleteOutlined,
	DownloadOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	LeftOutlined,
	MinusOutlined,
	PlusOutlined,
	UnorderedListOutlined,
} from "@ant-design/icons";
import { formatPrice } from "@di-devs/utils-fe";
import {
	Badge,
	Button,
	Card,
	Col,
	DatePicker,
	Modal,
	message,
	Radio,
	Row,
	Select,
	Table,
} from "antd";
import type dayjs from "dayjs";
import type React from "react";
import { useMemo, useState } from "react";
import { EVENT_MESSAGES } from "../../../shared/constants/event.constants";
import {
	EventImageCarousel,
	ReservationStatusCard,
	ScheduleCalendar,
} from "../components/ui";
import {
	ManualReservationModal,
	OccurrenceDetailModal,
	ReservationExportModal,
	TimeSlotAdditionModal,
} from "../containers";
import {
	useCancelRegistration,
	useDeleteOccurrence,
	useDeleteOccurrencesBatch,
	useOccurrenceDetail,
	useUpdateOccurrenceVisibility,
} from "../hooks";
import type { EventDetailData, EventOccurrence } from "../types";
import { CALENDAR_SLOT_DURATION_OPTIONS } from "../types";
import { toJSTDayjs } from "../utils";

interface EventDetailPageProps {
	eventData?: EventDetailData & { fee?: number };
	loading?: boolean;
	primaryColor?: string;
	primaryLightColor?: string;
	imageBaseUrl?: string;
	systemType?: string;
	onBack?: () => void;
	onEditEvent?: () => void;
	onDeleteEvent?: () => void;
}

type ViewMode = "calendar" | "table";

export const EventDetailPage: React.FC<EventDetailPageProps> = ({
	eventData,
	loading = false,
	primaryColor = "#99CA29",
	primaryLightColor = "#fdfff0",
	imageBaseUrl = "",
	systemType = "イベント",
	onBack,
	onEditEvent,
	onDeleteEvent,
}) => {
	const [viewMode, setViewMode] = useState<ViewMode>("calendar");
	const [slotDuration, setSlotDuration] = useState(60);

	const [isManualReservationOpen, setIsManualReservationOpen] = useState(false);
	const [isTimeSlotAdditionOpen, setIsTimeSlotAdditionOpen] = useState(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState(false);
	const [isTimeSlotDeletionMode, setIsTimeSlotDeletionMode] = useState(false);
	const [deletionDateRange, setDeletionDateRange] = useState<
		[dayjs.Dayjs | null, dayjs.Dayjs | null] | null
	>(null);
	const [isOccurrenceDetailOpen, setIsOccurrenceDetailOpen] = useState(false);
	const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<
		number | undefined
	>();

	const { data: occurrenceDetail } = useOccurrenceDetail(
		isOccurrenceDetailOpen ? selectedOccurrenceId : undefined,
	);
	const updateVisibilityMutation = useUpdateOccurrenceVisibility();
	const deleteOccurrenceMutation = useDeleteOccurrence();
	const deleteOccurrencesBatchMutation = useDeleteOccurrencesBatch();
	const cancelRegistrationMutation = useCancelRegistration();

	const handleDeleteEvent = () => {
		Modal.confirm({
			title: "確認",
			icon: <ExclamationCircleOutlined style={{ color: "#dc2626" }} />,
			content: (
				<p>
					<span style={{ color: "#dc2626" }}>{eventData?.title || ""}</span>
					{EVENT_MESSAGES.CONFIRM_DELETE(systemType)}
				</p>
			),
			okText: "削除",
			okButtonProps: {
				size: "large",
				type: "primary",
				danger: true,
			},
			cancelText: "閉じる",
			cancelButtonProps: {
				size: "large",
			},
			centered: true,
			onOk: () => {
				onDeleteEvent?.();
			},
		});
	};

	const tableColumns = [
		{
			title: "日時",
			dataIndex: "startAt",
			key: "startAt",
			render: (startAt: string) =>
				toJSTDayjs(startAt).format("YYYY/MM/DD HH:mm"),
		},
		{
			title: "予約数",
			dataIndex: "sumExpected",
			key: "sumExpected",
			render: (sumExpected: number, record: EventOccurrence) =>
				`${sumExpected}/${record.maxAttendees}`,
		},
		{
			title: "参加者数",
			dataIndex: "sumAttended",
			key: "sumAttended",
		},
		{
			title: "空き状況",
			key: "status",
			render: (_: unknown, record: EventOccurrence) => {
				const left = record.maxAttendees - record.sumExpected;
				if (left <= 0) return <span style={{ color: "#dc2626" }}>満席</span>;
				if (left <= 3)
					return <span style={{ color: "#f59e0b" }}>残りわずか</span>;
				return <span style={{ color: "#10b981" }}>空席あり</span>;
			},
		},
	];

	const images = useMemo(() => {
		if (!eventData) return [];
		if (eventData.occasionImages && eventData.occasionImages.length > 0) {
			return eventData.occasionImages;
		}
		if (eventData.heroImageUrl) {
			return [{ picUrl: eventData.heroImageUrl, showOrder: 0 }];
		}
		if (eventData.gallery && eventData.gallery.length > 0) {
			return eventData.gallery.map((url, i) => ({ picUrl: url, showOrder: i }));
		}
		return [];
	}, [eventData]);

	if (!eventData) {
		return (
			<div style={{ padding: "24px", textAlign: "center" }}>
				{loading ? "読み込み中..." : "イベントが見つかりません"}
			</div>
		);
	}

	const buttonStyle = {
		backgroundColor: primaryColor,
		borderColor: primaryColor,
	};

	return (
		<div>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					marginBottom: "16px",
					gap: "8px",
					color: "#6b7280",
					fontSize: "14px",
				}}
			>
				<button
					type="button"
					className="cursor-pointer bg-transparent border-none p-0 text-inherit"
					onClick={onBack}
				>
					{systemType}一覧
				</button>
				<span aria-hidden="true">/</span>
				<h1 className="text-xl font-bold m-0">{eventData.title}</h1>
			</div>

			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: "24px",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
					<Button icon={<LeftOutlined />} onClick={onBack}>
						戻る
					</Button>
					<h1 style={{ margin: 0, fontSize: "24px", fontWeight: "bold" }}>
						{systemType}
					</h1>
				</div>
				<div style={{ display: "flex", gap: "8px" }}>
					<Button
						type="primary"
						icon={<ContactsOutlined />}
						onClick={() => setIsManualReservationOpen(true)}
						style={buttonStyle}
					>
						予約情報登録
					</Button>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => setIsTimeSlotAdditionOpen(true)}
						style={buttonStyle}
					>
						時間追加
					</Button>
					<Button
						type="primary"
						danger
						icon={<MinusOutlined />}
						onClick={() => {
							setDeletionDateRange(null);
							setIsTimeSlotDeletionMode(true);
						}}
					>
						時間削除
					</Button>
					<Button
						type="primary"
						icon={<EditOutlined />}
						onClick={onEditEvent}
						style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
					>
						{systemType}編集
					</Button>
					<Button
						type="primary"
						danger
						icon={<DeleteOutlined />}
						onClick={handleDeleteEvent}
					>
						{systemType}削除
					</Button>
					<Button
						icon={<DownloadOutlined />}
						onClick={() => setIsExportModalOpen(true)}
					>
						予約リストCSV
					</Button>
				</div>
			</div>

			<Row gutter={[32, 16]}>
				<Col xl={8} lg={10} md={12} xs={24}>
					<Badge.Ribbon
						text={
							eventData.isPublished ? (
								<span>
									<EyeOutlined /> 表示中
								</span>
							) : (
								<span>
									<EyeInvisibleOutlined /> 非表示中
								</span>
							)
						}
						color={eventData.isPublished ? "#22c55e" : "#ef4444"}
					>
						<Card variant="borderless">
							<EventImageCarousel
								images={images}
								title={eventData.title}
								imageBaseUrl={imageBaseUrl}
								primaryColor={primaryColor}
							/>

							{eventData.description && (
								<div style={{ marginTop: "24px", textAlign: "center" }}>
									<p style={{ whiteSpace: "pre-wrap" }}>
										{eventData.description}
									</p>
								</div>
							)}

							<div
								style={{
									marginTop: "24px",
									padding: "16px",
									borderRadius: "8px",
									backgroundColor: primaryLightColor,
									border: `1px solid ${primaryColor}`,
								}}
							>
								<p
									style={{
										textAlign: "center",
										fontWeight: "bold",
										marginBottom: "8px",
										color: primaryColor,
									}}
								>
									参加費
								</p>
								<p
									style={{
										textAlign: "center",
										fontSize: "24px",
										fontWeight: "bold",
										color: "#374151",
									}}
								>
									{formatPrice(eventData.fee)}
								</p>
							</div>

							<div
								style={{
									marginTop: "16px",
									padding: "16px",
									borderRadius: "8px",
									backgroundColor: primaryLightColor,
									border: `1px solid ${primaryColor}`,
								}}
							>
								<p
									style={{
										textAlign: "center",
										fontWeight: "bold",
										marginBottom: "8px",
										color: primaryColor,
									}}
								>
									対象者
								</p>
								<p
									style={{
										textAlign: "center",
										fontSize: "18px",
										fontWeight: "bold",
										color: "#374151",
									}}
								>
									{eventData.membershipScope === "member_and_guest"
										? "全員（会員・非会員）"
										: "会員のみ"}
								</p>
							</div>

							<ReservationStatusCard
								reservationCount={eventData.reservationCount}
								attendeeCount={eventData.attendeeCount}
								maximumAttendees={eventData.maximumAttendees}
								primaryColor={primaryColor}
								primaryLightColor={primaryLightColor}
							/>
						</Card>
					</Badge.Ribbon>
				</Col>

				<Col xl={16} lg={14} md={12} xs={24}>
					<div
						style={{
							border: `1px solid ${primaryColor}`,
							borderRadius: "4px",
							height: "100%",
						}}
					>
						<Card
							variant="borderless"
							title={`${systemType}プログラム一覧`}
							extra={
								<div
									style={{ display: "flex", alignItems: "center", gap: "16px" }}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}
									>
										<span>1枠の時間：</span>
										<Select
											value={slotDuration}
											onChange={setSlotDuration}
											style={{ width: "100px" }}
										>
											{CALENDAR_SLOT_DURATION_OPTIONS.map((opt) => (
												<Select.Option key={opt.value} value={opt.value}>
													{opt.label}
												</Select.Option>
											))}
										</Select>
									</div>
									<Radio.Group
										value={viewMode}
										onChange={(e) => setViewMode(e.target.value)}
									>
										<Radio.Button value="calendar">
											<CalendarOutlined />
										</Radio.Button>
										<Radio.Button value="table">
											<UnorderedListOutlined />
										</Radio.Button>
									</Radio.Group>
								</div>
							}
						>
							{viewMode === "calendar" ? (
								<ScheduleCalendar
									occurrences={eventData.occurrences || []}
									slotDuration={slotDuration}
									primaryColor={primaryColor}
									primaryLightColor={primaryLightColor}
									onSlotClick={(occurrence) => {
										setSelectedOccurrenceId(occurrence.occurrenceId);
										setIsOccurrenceDetailOpen(true);
									}}
								/>
							) : (
								<Table
									dataSource={eventData.occurrences || []}
									columns={tableColumns}
									rowKey="occurrenceId"
									pagination={{ pageSize: 10 }}
									loading={loading}
								/>
							)}
						</Card>
					</div>
				</Col>
			</Row>

			<ManualReservationModal
				open={isManualReservationOpen}
				onClose={() => setIsManualReservationOpen(false)}
				occurrences={eventData.occurrences || []}
				eventTitle={eventData.title}
				primaryColor={primaryColor}
				primaryLightColor={primaryLightColor}
			/>

			<TimeSlotAdditionModal
				open={isTimeSlotAdditionOpen}
				onClose={() => setIsTimeSlotAdditionOpen(false)}
				eventOccasionId={eventData.eventOccasionId}
				existingOccurrences={eventData.occurrences}
				primaryColor={primaryColor}
				primaryLightColor={primaryLightColor}
			/>

			<ReservationExportModal
				open={isExportModalOpen}
				onClose={() => setIsExportModalOpen(false)}
				eventOccasionId={eventData.eventOccasionId}
				eventTitle={eventData.title}
				primaryColor={primaryColor}
				primaryLightColor={primaryLightColor}
			/>

			<OccurrenceDetailModal
				open={isOccurrenceDetailOpen}
				onClose={() => {
					setIsOccurrenceDetailOpen(false);
					setSelectedOccurrenceId(undefined);
				}}
				occurrence={
					occurrenceDetail
						? {
								occurrenceId: occurrenceDetail.occurrenceId,
								eventOccasionId: occurrenceDetail.eventOccasionId,
								startAt: occurrenceDetail.startAt,
								endAt: occurrenceDetail.endAt,
								maxAttendees: occurrenceDetail.maxAttendees,
								sumExpected: occurrenceDetail.sumExpected,
								sumAttended: occurrenceDetail.sumAttended,
								remarks: occurrenceDetail.remarks,
								isDisplayed: occurrenceDetail.isDisplayed,
								registrations: occurrenceDetail.registrations,
								cancels: occurrenceDetail.cancels,
							}
						: undefined
				}
				onUpdateVisibility={async (occurrenceId, isDisplayed) => {
					try {
						await updateVisibilityMutation.mutateAsync({
							occurrenceId,
							isDisplayed,
						});
						message.success(
							`時間を${isDisplayed ? "表示" : "非表示"}に設定しました`,
						);
					} catch {
						message.error("設定に失敗しました");
					}
				}}
				onDelete={async (occurrenceId) => {
					try {
						await deleteOccurrenceMutation.mutateAsync(occurrenceId);
						message.success("時間を削除しました");
						setIsOccurrenceDetailOpen(false);
						setSelectedOccurrenceId(undefined);
					} catch {
						message.error("削除に失敗しました");
					}
				}}
				onCancelRegistration={async (registrationId) => {
					try {
						await cancelRegistrationMutation.mutateAsync(registrationId);
						message.success("予約をキャンセルしました");
					} catch {
						message.error("キャンセルに失敗しました");
					}
				}}
				primaryColor={primaryColor}
				primaryLightColor={primaryLightColor}
			/>

			<Modal
				title="時間一括削除"
				open={isTimeSlotDeletionMode}
				onCancel={() => setIsTimeSlotDeletionMode(false)}
				okText="削除"
				cancelText="閉じる"
				okButtonProps={{
					danger: true,
					loading: deleteOccurrencesBatchMutation.isPending,
					disabled: !deletionDateRange?.[0] || !deletionDateRange?.[1],
				}}
				onOk={async () => {
					if (!deletionDateRange?.[0] || !deletionDateRange?.[1]) return;
					const from = deletionDateRange[0].startOf("day");
					const to = deletionDateRange[1].endOf("day");
					const idsToDelete = (eventData.occurrences || [])
						.filter((occ) => {
							const start = toJSTDayjs(occ.startAt);
							return (
								(start.isAfter(from) || start.isSame(from)) &&
								(start.isBefore(to) || start.isSame(to))
							);
						})
						.map((occ) => occ.occurrenceId);

					if (idsToDelete.length === 0) {
						message.warning("指定期間に時間枠がありません");
						return;
					}

					try {
						await deleteOccurrencesBatchMutation.mutateAsync({
							occurrenceIds: idsToDelete,
						});
						message.success(`${idsToDelete.length}件の時間を削除しました`);
						setIsTimeSlotDeletionMode(false);
					} catch {
						message.error("削除に失敗しました");
					}
				}}
				centered
			>
				<p style={{ marginBottom: "16px" }}>
					削除する期間を選択してください。期間内のすべての時間枠が削除されます。
				</p>
				<DatePicker.RangePicker
					value={deletionDateRange}
					onChange={(dates) => setDeletionDateRange(dates)}
					style={{ width: "100%" }}
					format="YYYY/MM/DD"
				/>
				{deletionDateRange?.[0] && deletionDateRange?.[1] && (
					<p style={{ marginTop: "8px", color: "#6b7280" }}>
						対象：
						{
							(eventData.occurrences || []).filter((occ) => {
								const start = toJSTDayjs(occ.startAt);
								const from = deletionDateRange[0]!.startOf("day");
								const to = deletionDateRange[1]!.endOf("day");
								return (
									(start.isAfter(from) || start.isSame(from)) &&
									(start.isBefore(to) || start.isSame(to))
								);
							}).length
						}
						件の時間枠
					</p>
				)}
			</Modal>
		</div>
	);
};
